import { useEffect, useMemo, useState } from 'react';
import type { SubmissionColumn } from '../../api/forms';
import type { DeliveryFilter, FilterCondition, FilterOp, SubmissionFilters } from '../../shared/types';

/** Operators follow the field's type — a date range makes no sense on a number. */
const OPS_BY_TYPE: Record<string, { op: FilterOp; label: string }[]> = {
  text: [
    { op: 'contains', label: 'contains' },
    { op: 'is', label: 'is' },
    { op: 'isnot', label: 'is not' },
    { op: 'notcontains', label: 'does not contain' },
    { op: 'empty', label: 'is empty' },
    { op: 'notempty', label: 'is not empty' },
  ],
  choice: [
    { op: 'is', label: 'is' },
    { op: 'isnot', label: 'is not' },
    { op: 'empty', label: 'is empty' },
    { op: 'notempty', label: 'is not empty' },
  ],
  number: [
    { op: 'gt', label: 'is more than' },
    { op: 'lt', label: 'is less than' },
    { op: 'gte', label: 'is at least' },
    { op: 'lte', label: 'is at most' },
    { op: 'is', label: 'is exactly' },
    { op: 'empty', label: 'is empty' },
  ],
  date: [
    { op: 'after', label: 'is after' },
    { op: 'before', label: 'is before' },
    { op: 'empty', label: 'is empty' },
    { op: 'notempty', label: 'is not empty' },
  ],
  identity: [
    { op: 'contains', label: 'contains' },
    { op: 'is', label: 'is' },
    { op: 'empty', label: 'is empty' },
  ],
};

const opsFor = (col?: SubmissionColumn) => OPS_BY_TYPE[col?.type || 'text'] || OPS_BY_TYPE.text;

/**
 * The words an operator is shown as, wherever it appears — the panel's select
 * and the chip that summarises it. A chip reading "gte" would make the reader
 * translate a token the UI never asked them to learn.
 */
export const opLabel = (op: FilterOp): string => {
  for (const list of Object.values(OPS_BY_TYPE)) {
    const hit = list.find((o) => o.op === op);
    if (hit) return hit.label;
  }
  return op;
};

/** An operator that stands on its own — no value to type. */
const NEEDS_NO_VALUE = new Set<FilterOp>(['empty', 'notempty']);

/** A row only counts once it can actually be evaluated. */
export const conditionComplete = (c: FilterCondition): boolean =>
  !!c.column && !!c.op && (NEEDS_NO_VALUE.has(c.op) || String(c.value ?? '').trim() !== '');

const DELIVERY_OPTIONS: { value: DeliveryFilter; label: string; bad?: boolean }[] = [
  { value: 'any', label: 'Any' },
  { value: 'any_failed', label: 'Any failed', bad: true },
  { value: 'pending', label: 'Pending' },
  { value: 'all_delivered', label: 'All delivered' },
  { value: 'none_sent', label: 'Nothing sent' },
];

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** The date presets, as the range each one means. */
function preset(kind: string): { from?: string; to?: string } {
  const now = new Date();
  const days = (n: number) => isoDay(new Date(now.getTime() - n * 86_400_000));
  switch (kind) {
    case 'today': return { from: isoDay(now), to: isoDay(now) };
    case '7': return { from: days(7), to: isoDay(now) };
    case '30': return { from: days(30), to: isoDay(now) };
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      return { from: isoDay(new Date(now.getFullYear(), q * 3, 1)), to: isoDay(now) };
    }
    default: return {};
  }
}

/**
 * The filter panel.
 *
 * Edits a DRAFT and only hands it back on Apply, so half-typed conditions never
 * reload the table underneath the person writing them. The live match count is
 * the exception — that has to reflect the draft to be worth showing.
 */
export default function FilterPanel({ columns, filters, connections, matches, counting, onCount, onApply, onClear }: {
  columns: SubmissionColumn[];
  filters: SubmissionFilters;
  connections: { id: number; name: string; type: string }[];
  matches: number | null;
  counting: boolean;
  onCount: (draft: SubmissionFilters) => void;
  onApply: (draft: SubmissionFilters) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = useState<SubmissionFilters>(filters);
  useEffect(() => { setDraft(filters); }, [filters]);

  // Only complete rows are ever counted or applied.
  const usable = useMemo(
    () => ({ ...draft, conditions: (draft.conditions || []).filter(conditionComplete) }),
    [draft]
  );
  useEffect(() => { onCount(usable); }, [usable, onCount]);

  const set = (patch: Partial<SubmissionFilters>) => setDraft({ ...draft, ...patch });
  const rows = draft.conditions || [];
  const setRow = (i: number, patch: Partial<FilterCondition>) =>
    set({ conditions: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  const activePreset = ['today', '7', '30', 'quarter'].find((k) => {
    const p = preset(k);
    return p.from === draft.from && p.to === draft.to;
  });

  // Not every column can be filtered on — the deliveries pill has its own
  // control above, and there is nothing to compare it to.
  const filterable = columns.filter((c) => c.type !== 'deliveries');

  return (
    <>
      <div className="sl-pop-head">Filter submissions</div>
      <div className="sl-pop-body">
        <div className="sl-section">
          <div className="sl-group" style={{ marginTop: 0 }}>Delivery status</div>
          <div className="sl-pills">
            {DELIVERY_OPTIONS.map((o) => {
              const on = (draft.delivery || 'any') === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  className={`sl-fpill ${on ? 'on' : ''} ${o.bad ? 'bad' : ''}`}
                  onClick={() => set({ delivery: o.value === 'any' ? undefined : o.value })}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            <span className="sl-fp-word">at</span>
            <select
              className="lg-ctl"
              value={String(draft.connectionId || '')}
              onChange={(e) => set({ connectionId: e.target.value ? Number(e.target.value) : undefined })}
            >
              <option value="">any connection</option>
              {connections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span className="sl-fp-faint">leave as <i>any connection</i> to ignore which one</span>
          </div>
        </div>

        <div className="sl-section">
          <div className="sl-group" style={{ marginTop: 0 }}>Received</div>
          <div className="sl-pills">
            {[['today', 'Today'], ['7', '7 days'], ['30', '30 days'], ['quarter', 'This quarter']].map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`sl-fpill ${activePreset === k ? 'on' : ''}`}
                onClick={() => set(preset(k))}
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              className={`sl-fpill ${draft.from && !activePreset ? 'on' : ''}`}
              onClick={() => set({ from: draft.from || isoDay(new Date()), to: draft.to })}
            >
              Custom…
            </button>
          </div>
          <div className="sl-daterow">
            <input type="date" className="lg-ctl" value={draft.from || ''} onChange={(e) => set({ from: e.target.value || undefined })} />
            <span className="sl-fp-faint">→</span>
            <input type="date" className="lg-ctl" value={draft.to || ''} onChange={(e) => set({ to: e.target.value || undefined })} />
          </div>
        </div>

        <div className="sl-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div className="sl-group" style={{ margin: 0, flex: 1 }}>Answers</div>
            <span className="sl-fp-word">Match</span>
            <select
              className="lg-ctl"
              style={{ width: 84 }}
              value={draft.match || 'all'}
              onChange={(e) => set({ match: e.target.value as 'all' | 'any' })}
            >
              <option value="all">all</option>
              <option value="any">any</option>
            </select>
            <span className="sl-fp-word">of these</span>
          </div>

          {rows.map((row, i) => {
            const col = columns.find((c) => c.id === row.column);
            const ops = opsFor(col);
            const complete = conditionComplete(row);
            const noValue = NEEDS_NO_VALUE.has(row.op);
            return (
              <div className={`sl-cond ${complete ? '' : 'draft'}`} key={i}>
                <select
                  value={row.column}
                  onChange={(e) => {
                    // Changing the field can invalidate the operator, so it
                    // falls back to that type's first one.
                    const next = columns.find((c) => c.id === e.target.value);
                    setRow(i, { column: e.target.value, op: opsFor(next)[0].op, value: '' });
                  }}
                >
                  <option value="">— pick a field —</option>
                  {filterable.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}{c.orphan ? ' (no longer on the form)' : ''}</option>
                  ))}
                </select>
                <select value={row.op} onChange={(e) => setRow(i, { op: e.target.value as FilterOp })}>
                  {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                </select>
                {noValue ? (
                  <span className="sl-fp-faint">—</span>
                ) : col?.options?.length ? (
                  <select value={String(row.value ?? '')} onChange={(e) => setRow(i, { value: e.target.value })}>
                    <option value="">— pick —</option>
                    {col.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    type={col?.type === 'number' ? 'number' : col?.type === 'date' ? 'date' : 'text'}
                    value={String(row.value ?? '')}
                    placeholder="value"
                    onChange={(e) => setRow(i, { value: e.target.value })}
                  />
                )}
                {/* F3': the × keeps its place on a desktop; on a phone, where
                    the row is one column, it becomes the word. Exactly one of
                    the two is ever rendered, so the button is named "Remove"
                    at every width. */}
                <button className="drop" title="Remove" onClick={() => set({ conditions: rows.filter((_, j) => j !== i) })}>
                  <span aria-hidden="true" className="drop-x">×</span>
                  <span className="drop-word">Remove</span>
                </button>
              </div>
            );
          })}

          <button
            className="fs-btn text sm"
            style={{ padding: '4px 0' }}
            onClick={() => set({ conditions: [...rows, { column: '', op: 'contains', value: '' }] })}
          >
            + Add a condition
          </button>
          <div className="sl-fp-note">
            Operators follow the field's type — text gets <i>is / contains / is empty</i>, numbers get comparisons, choice
            fields list their options, dates get a range. Half-finished rows are ignored until complete, the same rule the
            Logic and Connections mapping rows use.
          </div>
        </div>
      </div>

      <div className="sl-pop-foot">
        <button type="button" className="fs-btn text sm muted" onClick={onClear}>Clear all</button>
        <span style={{ flex: 1 }} />
        <span className="sl-fp-word">
          {counting ? 'Counting…' : matches === null ? '' : <>Matches <b>{matches.toLocaleString()}</b></>}
        </span>
        <button className="fs-btn primary sm" onClick={() => onApply(usable)}>Apply</button>
      </div>
    </>
  );
}
